import type Stripe from 'stripe';
import type { StripeProduct } from '~/lib/.server/llm/providers/types';
import { SUPABASE_EDGE_FUNCTIONS_FOLDER } from '~/lib/constants';
import type { FileMap } from '~/lib/stores/files';
import { addWorkDir } from '~/utils/workDir';

const PRODUCT_REGEX = /\bprod_[A-Za-z0-9]{14,}\b/g;
const STRIPE_KEY_REGEX = /\bsk_(test|live)_[A-Za-z0-9]{14,}\b/g;

/**
 * An helper function to return an API key that
 * can be shown on the screen that does not contains
 * the entire content of the key.
 */
export function hidePartOfApiKey(key: string) {
  if (!key || key.length < 13) {
    return key;
  }

  return `${key.slice(0, 10)}${'•'.repeat(5)}${key.slice(-3)}`;
}

export function sanitizeStripeSecretKey(text: string) {
  const matches = text.matchAll(STRIPE_KEY_REGEX);

  let result = '';
  let index = 0;

  const keysFound = new Set<string>();

  for (const match of matches) {
    const key = match[0];
    const start = match.index ?? 0;
    const end = start + key.length;
    const beforeMatch = text.slice(index, start);
    const sanitizedKey = hidePartOfApiKey(key);

    result += beforeMatch + sanitizedKey;
    index = end;

    keysFound.add(key);
  }

  return [result + text.slice(index), keysFound] as const;
}

export function injectProductInfo(products: Stripe.Product[], stripeConfigured: boolean, messageContent?: string) {
  if (!messageContent || !stripeConfigured) {
    return undefined;
  }

  const matches = messageContent.matchAll(PRODUCT_REGEX);

  const validProductIds = [];
  const invalidProductIds = [];

  const knownProducts = new Map(products.map((product) => [product.id, product]));

  for (const match of matches) {
    const productId = match[0];
    const product = knownProducts.get(productId);

    if (!product || product.default_price == null) {
      invalidProductIds.push(productId);
    } else {
      validProductIds.push(productId);
    }
  }

  if (validProductIds.length === 0 && invalidProductIds.length === 0) {
    return undefined;
  }

  const result = [];

  if (validProductIds.length > 0) {
    result.push(
      'Here are the details for the products provided:',
      '',
      formatProductInfoAsMessage(validProductIds.map((id) => knownProducts.get(id)!) as StripeProduct[]),
    );

    if (invalidProductIds.length > 0) {
      result.push('');
    }
  }

  if (invalidProductIds.length > 0) {
    result.push(
      'The following product ids could not be found on Stripe or do not have a default price set:',
      '',
      ...invalidProductIds,
      '',
      'DO NOT integrate them using made up values for that product and instead inform the user that they cannot be added or removed.',
    );
  }

  return result.join('\n');
}

export function formatProductInfoAsMessage(products: StripeProduct[]) {
  return products
    .map(({ id, description, name, default_price: defaultPrice }) =>
      [
        `- Product '${name}':`,
        `  - 'id': ${id}`,
        `  - 'priceId': ${defaultPrice.id}`,
        description ? `    - 'description': ${description}` : null,
        `    - 'mode': ${convertToMode(defaultPrice.type)}`,
      ]
        .filter(Boolean)
        .join('\n'),
    )
    .join('\n');
}

function convertToMode(type: 'one_time' | 'recurring') {
  return type === 'one_time' ? 'payment' : 'subscription';
}

export function extractModeFromStripeSecretKey(key: string | undefined) {
  const isTestKey = key?.startsWith('sk_test_');
  const isProdKey = key?.startsWith('sk_live_');

  return isTestKey ? 'test' : isProdKey ? 'live' : undefined;
}

export function formatDefaultPriceForProduct(product: Stripe.Product) {
  return typeof product.default_price === 'string' || !product.default_price
    ? product.default_price
    : formatStripePrice(product.default_price);
}

/**
 * List of zero-decimal currencies.
 *
 * @see https://docs.stripe.com/currencies#zero-decimal
 */
const zeroDecimalCurrencies = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

interface CurrencyConfig {
  symbol: string;
  afterAmount?: boolean;
}

const currencyConfigs: Record<string, CurrencyConfig> = {
  USD: { symbol: '$' },
  EUR: { symbol: '€', afterAmount: true },
  GBP: { symbol: '£' },
  JPY: { symbol: '¥' },
  CNY: { symbol: '¥' },
  AUD: { symbol: 'A$' },
  CAD: { symbol: 'C$' },
  CHF: { symbol: 'CHF', afterAmount: true },
  INR: { symbol: '₹' },
  BRL: { symbol: 'R$' },
  RUB: { symbol: '₽', afterAmount: true },
  KRW: { symbol: '₩' },
  SGD: { symbol: 'S$' },
  NZD: { symbol: 'NZ$' },
  MXN: { symbol: 'MX$' },
  HKD: { symbol: 'HK$' },
  PLN: { symbol: 'zł', afterAmount: true },
  CZK: { symbol: 'Kč', afterAmount: true },
  SEK: { symbol: 'kr', afterAmount: true },
  DKK: { symbol: 'kr', afterAmount: true },
  NOK: { symbol: 'kr', afterAmount: true },
};

/**
 * Formats a Stripe price amount with the correct decimal places and currency symbol.
 */
export function formatStripePrice({ unit_amount: amount, currency }: Stripe.Price): string {
  if (typeof amount !== 'number') {
    return '';
  }

  const upperCurrency = currency.toUpperCase();
  const isZeroDecimal = zeroDecimalCurrencies.has(upperCurrency);
  const currencyConfig = currencyConfigs[upperCurrency];

  const numericAmount = isZeroDecimal ? amount : amount / 100;

  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: isZeroDecimal ? 0 : 2,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  }).format(numericAmount);

  const symbol = currencyConfig?.symbol || upperCurrency;
  const shouldPlaceAfter = currencyConfig?.afterAmount ?? false;

  return shouldPlaceAfter ? `${formattedAmount} ${symbol}` : `${symbol}${formattedAmount}`;
}

const STRIPE_PATH_PATTERN = /(\bs|S)tripe(\b|[A-Z])|(\b|_)STRIPE(\b|_)/g;
const STRIPE_PAYMENT_LINKS = /https:\/\/buy\.stripe\.com\/\w+/g;

const SUPABASE_FUNCTIONS_FOLDER = addWorkDir(SUPABASE_EDGE_FUNCTIONS_FOLDER) + '/';

export function isStripeConfiguredInThisApp(files: FileMap) {
  /**
   * Detect the presence of payment link or if there are any edge
   * functions named stripe already.
   */
  for (const path in files) {
    const dirent = files[path];

    // edge functinon including "stripe" in the name
    if (dirent?.type === 'folder' && path.startsWith(SUPABASE_FUNCTIONS_FOLDER) && path.match(STRIPE_PATH_PATTERN)) {
      return true;
    }

    // file including a payment link
    if (dirent?.type === 'file' && dirent.content.match(STRIPE_PAYMENT_LINKS)) {
      return true;
    }
  }

  return false;
}
